import { Component, inject } from '@angular/core';
import { CommonModule, PlatformLocation } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { ToastrService } from 'ngx-toastr';
import { ActionItemComponent } from '../../shared/action-item.component';
import { GroupsService } from '../groups.service';
import { SchoolsService } from '../../schools/schools.service';
import { map } from 'rxjs';

@Component({
  selector: 'oc-assign-teacher',
  standalone: true,
  imports: [CommonModule, RouterModule, ActionItemComponent, ReactiveFormsModule, FormlyBootstrapModule, FormlyModule],
  template: `
    <div class="modal-container">
      <div class="modal-spacer"></div>
      <div class="modal-body">
        <h1>Assign Teacher</h1>
        <form [formGroup]="form" (ngSubmit)="onSubmit(model)">
          <formly-form [form]="form" [fields]="fields" [model]="model" [options]="options"></formly-form>
          <div class="d-flex gap-2">
            <button type="submit" class="btn btn-primary mt-3 mb-4">
              <span *ngIf="saving" class="spinner-border spinner-border-sm"></span>
              <span *ngIf="!saving">Save Changes</span>
            </button>
            <button type="button" class="btn btn-secondary mt-3 mb-4" (click)="activeModal.dismiss('Cancel')">
              Cancel
            </button>
          </div>
        </form>
      </div>
      <div class="modal-spacer">
        <oc-action-item icon="tablerX" size="lg" (click)="activeModal.dismiss('Cross click')"></oc-action-item>
      </div>
    </div>
  `,
  styles: `
    .modal-container {
      display: flex;
      min-height: 100vh;
    }

    .modal-body {
      flex: 0;
      flex-basis: 560px;
      padding: 2.5rem 3.5rem;
    }

    .modal-spacer {
      flex: 1; 
    }

    .modal-spacer:last-child {
      padding: 2rem;
      display: flex;
      justify-content: flex-end;
      align-items: flex-start;
    }

    h1 {
      margin-bottom: 2rem;
    }
  `,
})
export class AssignTeacherComponent {
  private platformLocation = inject(PlatformLocation);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);
  private schoolsService = inject(SchoolsService);
  private groupsService = inject(GroupsService);
  public activeModal = inject(NgbActiveModal);

  public groupId: string | null = null;
  public schoolId: string | null = null;

  saving = false;
  options: FormlyFormOptions = {
    formState: {
      submitted: false,
    },
  };

  form = new FormGroup({});
  model = { userId: '' };
  fields: FormlyFieldConfig[] = [
    {
      key: 'userId',
      type: 'typeahead',
      props: {
        label: 'User',
        required: true,
        placeholder: 'Search',
        search$: (term: string) => {
          return this.schoolsService.getSchoolUsers(this.schoolId!).pipe(
            map((users) => {
              return users.filter(
                (user) =>
                  (user.firstName.toLowerCase() + ' ' + user.lastName.toLowerCase()).indexOf(term.toLowerCase()) !==
                    -1 || !term,
              );
            }),
            map((users) => users.map((user) => ({ value: user.id, label: `${user.firstName} ${user.lastName}` }))),
          );
        },
      },
    },
  ];

  constructor() {
    this.platformLocation.onPopState(() => this.modalService.dismissAll());
  }

  onSubmit(model: any) {
    this.options.formState.submitted = true;
    if (this.saving) {
      return;
    }
    if (!this.form.valid) {
      this.toastr.error('Please review and try again');
      return;
    }

    this.saving = true;

    this.groupsService.assignTeacher(this.groupId!, model.userId.value).subscribe((response) => {
      this.toastr.success('Teacher added');
      this.activeModal.close(response);
    });
  }
}
